import { useEffect, useState } from 'react';
import { HandCard, type HandCardData } from '../HandCard.js';

export type RecycleCostModalProps = {
  /** What the coach is about to play, e.g. "Lead Smith + Contested Mark". */
  actionLabel: string;
  /** How many cards must be recycled (hand → deck) to pay for it. */
  cost: number;
  /**
   * The cards the coach may spend — their hand with the card(s) being PLAYED
   * already removed. Indices are stable for the life of the modal, so a
   * duplicate card id can be selected per-instance.
   */
  cards: HandCardData[];
  busy?: boolean;
  /** Confirm with the chosen card ids (length === cost). */
  onConfirm: (payIds: string[]) => void;
  onCancel: () => void;
};

/**
 * The recycle-payment picker. A costed play (action / weather modifier) returns
 * cards from hand to deck; this is where the COACH chooses exactly which ones —
 * any card, players included — rather than the engine auto-picking. Opens only
 * when there's a genuine choice (a forced pay resolves itself). Starts empty:
 * every payment card is selected deliberately (owner choice, 2026-06-17).
 *
 * Confirm is locked until exactly `cost` cards are chosen. Closes on Escape,
 * backdrop click, or Cancel — none of which spends anything.
 */
export function RecycleCostModal({
  actionLabel,
  cost,
  cards,
  busy = false,
  onConfirm,
  onCancel,
}: RecycleCostModalProps) {
  // Selection is by INDEX (a hand can hold duplicate card ids); order of
  // selection is preserved so the coach reads what they've picked so far.
  const [selected, setSelected] = useState<number[]>([]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const toggle = (i: number) => {
    setSelected((cur) => {
      if (cur.includes(i)) return cur.filter((x) => x !== i);
      if (cur.length >= cost) return cur; // already at the cap — no-op
      return [...cur, i];
    });
  };

  const ready = selected.length === cost;
  const remaining = cost - selected.length;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-ec-ground/80 p-4 backdrop-blur-sm"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-label="Choose cards to recycle"
    >
      <div
        className="flex max-h-full w-full max-w-2xl animate-pop-in flex-col overflow-hidden rounded-xl border border-ec-line bg-ec-panel/95 shadow-ec-panel motion-reduce:animate-none"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — what's being played + the price. */}
        <div className="shrink-0 border-b border-ec-line bg-ec-panel-2 px-4 py-3">
          <h2 className="font-display text-sm font-bold uppercase tracking-wide text-ec-brass">
            Pay the cost — recycle {cost} card{cost === 1 ? '' : 's'} to your deck
          </h2>
          <p className="mt-0.5 text-xs text-ec-bone">
            Playing <span className="font-semibold text-ec-face">{actionLabel}</span>. Choose
            which cards to send from your hand back into your deck.
          </p>
        </div>

        {/* The hand to spend from — scrolls inside the modal if it's a big hand. */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          <div className="flex flex-wrap justify-center gap-2">
            {cards.map((card, i) => {
              const isSel = selected.includes(i);
              const order = selected.indexOf(i) + 1;
              return (
                <div key={`${card.id ?? 'card'}-${i}`} className="relative">
                  <HandCard
                    card={card}
                    size="sm"
                    selected={isSel}
                    onClick={() => !busy && toggle(i)}
                  />
                  {isSel && (
                    <span className="pointer-events-none absolute -right-1 -top-1 z-10 flex h-5 min-w-5 items-center justify-center rounded-full border border-ec-ink/40 bg-ec-brass px-1 font-display text-[10px] font-bold leading-none text-ec-ink shadow">
                      ↩{cost > 1 ? order : ''}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer — counter + commit / cancel. */}
        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-ec-line bg-ec-panel-2 px-4 py-3">
          <span className="text-xs text-ec-bone">
            {ready ? (
              <span className="font-semibold text-ec-go">{cost} selected — ready</span>
            ) : (
              <>
                <span className="font-semibold text-ec-brass">{selected.length}</span> / {cost}{' '}
                selected · pick {remaining} more
              </>
            )}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded border border-ec-line px-3 py-1.5 font-display text-xs uppercase tracking-wide text-ec-bone transition hover:border-ec-bone"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!ready || busy}
              onClick={() => onConfirm(selected.map((i) => cards[i]!.id!).filter(Boolean))}
              className="rounded-md border border-ec-brass/60 bg-[rgba(245,168,28,0.18)] px-3 py-1.5 font-display text-xs font-semibold uppercase tracking-wide text-ec-brass transition hover:border-ec-brass hover:bg-[rgba(245,168,28,0.28)] disabled:cursor-not-allowed disabled:opacity-40"
            >
              Recycle &amp; play
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
